import * as React from "react";
import { Stack, TextField, TextFieldProps, AlertColor } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import CclDrawer from "../../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../../components/common/cclDrawer/cclDrawerActionBar";
import { cclTokenedPayrollApi } from "../../../../services/cclTokenedPayrollApi";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { AccessEventOFCDetails, AccessEventOFCReducedDetails } from "../../../../services/types/accessEventTypes";
import { UserClaimsService } from "../../../../services/currentUserService/currentUserService";

export interface WorkEntryFormValues {
    id?: string | number;
    workDate: Date | null;
    payDate: Date | null;
    projectKey: string;
    rev_Rec_Date: Date | null;
    eventName: string;
    EventNotes: string;
}

export interface ModifyWorkEntryDrawerProps {
    sessionId: string;
    newEntry: boolean;
    workEntry?: WorkEntryFormValues;
    projectId: string;
    projectCode: string;
    projectName: string;
    revRecDate?: string | Date | null;
    open: boolean;
    onClose: () => void;
    onShowSnackbar: (message: string, severity: AlertColor) => void;
}

const ModifyWorkEntryDrawer: React.FC<ModifyWorkEntryDrawerProps> = (props) => {
    
    const isEditingWorkEntry = props.workEntry != null;
    const { logEvent } = useLogAccessEvent();

    const toDate = React.useCallback((value: string | Date | null | undefined): Date | null => {
        if (!value) {
            return null;
        }

        const toLocalDate = (year: number, month: number, day: number): Date | null => {
            const date = new Date(year, month - 1, day);
            return Number.isNaN(date.getTime()) ? null : date;
        };

        if (typeof value === "string") {
            const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
            if (match) {
                return toLocalDate(Number(match[1]), Number(match[2]), Number(match[3]));
            }

            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        if (Number.isNaN(value.getTime())) {
            return null;
        }

        const utcMidnightTime = Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
        return value.getTime() === utcMidnightTime
            ? new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
            : value;
    }, []);

    const [dateOfWork, setDateOfWork] = React.useState<Date | null>(props.workEntry?.workDate ?? null);
    const [paymentDate, setPaymentDate] = React.useState<Date | null>(props.workEntry?.payDate ?? null);
    const [rev_Rec_Date, setRevRecDate] = React.useState<Date | null>(props.workEntry?.rev_Rec_Date ?? toDate(props.revRecDate));
    const [notes, setNotes] = React.useState<string>(props.workEntry?.EventNotes ?? "");

    const [addWorkEntry] = cclTokenedPayrollApi.useAddWorkEntryMutation();
    const [editWorkEntry] = cclTokenedPayrollApi.useEditWorkEntryMutation();

    const handleSave = async () => {
        const resolvedEventName = isEditingWorkEntry
            ? (props.workEntry?.eventName ?? props.projectName)
            : props.projectName;

        const workEntry: WorkEntryFormValues = {
            id: props.workEntry?.id,
            workDate: dateOfWork,
            payDate: paymentDate,
            projectKey: props.projectId,
            rev_Rec_Date,
            eventName: resolvedEventName,
            EventNotes: notes,
        };
        if (isEditingWorkEntry) {
            editWorkEntry(workEntry)
                .unwrap()
                .then((response) => {
                    const evtData: AccessEventOFCReducedDetails = {
                        sessionID: props.sessionId,
                        projectCode: props.projectCode,
                    };
                    logEvent("WorkEntryEdited", evtData);

                    props.onShowSnackbar("Work Entry Edited", "success");
                    props.onClose();
                })
                .catch((error) => {
                    console.error("Failed to update work entry:", error);

                    props.onShowSnackbar("Failed to update Work Entry", "error");
                });
        } else {
            addWorkEntry(workEntry)
                .unwrap()
                .then((response) => {
                    const evtData: AccessEventOFCReducedDetails = {
                        sessionID: props.sessionId,
                        projectCode: props.projectCode,
                    };
                    logEvent("WorkEntryCreated", evtData);

                    props.onShowSnackbar("Work Entry Added", "success");
                    props.onClose();
                })
                .catch((error) => {
                    console.error("Failed to save work entry:", error);

                    props.onShowSnackbar("Failed to add Work Entry", "error");
                }); 
        }
    }

    React.useEffect(() => {
        if (!props.open) {
            return;
        }

        if (props.workEntry) {
            setDateOfWork(props.workEntry.workDate ?? null);
            setPaymentDate(props.workEntry.payDate ?? null);
            setRevRecDate(props.workEntry.rev_Rec_Date ?? null);
            setNotes(props.workEntry.EventNotes ?? "");
            return;
        }

        setDateOfWork(null);
        setPaymentDate(null);
        setRevRecDate(toDate(props.revRecDate));
        setNotes("");
        //eslint-disable-next-line
    }, [props.open, props.workEntry, props.revRecDate, toDate]);

    const areDatesEqual = (first: Date | null, second: Date | null): boolean => {
        if (first == null && second == null) {
            return true;
        }

        if (first == null || second == null) {
            return false;
        }

        return first.getTime() === second.getTime();
    };

    const hasChanges =
        !areDatesEqual(dateOfWork, props.workEntry?.workDate ?? null) ||
        !areDatesEqual(paymentDate, props.workEntry?.payDate ?? null) ||
        !areDatesEqual(rev_Rec_Date, props.workEntry?.rev_Rec_Date ?? null) ||
        notes !== (props.workEntry?.EventNotes ?? "");

    const canSave = isEditingWorkEntry
        ? hasChanges
        : (
            dateOfWork != null &&
            paymentDate != null &&
            rev_Rec_Date != null
        );

    const updateDateOfWork = (date: any) => {
        setDateOfWork(date);
    }

    return (
        <CclDrawer 
            title={isEditingWorkEntry || !props.newEntry ? "Edit Work Entry" : "Add Work Entry"} 
            open={props.open} 
            onClose={props.onClose }
        >
            <Stack spacing={2}>
                <DatePicker
                    label={"Date of Work"}
                    value={dateOfWork}
                    onChange={(e) => updateDateOfWork(e)}
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                        params.fullWidth = true;
                        params.size = "small";
                        return <TextField {...params} id="work-entry-date-of-work" />;
                    }}
                />

                <DatePicker
                    label={"Payment Date"}
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e)}
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                        params.fullWidth = true;
                        params.size = "small";
                        return <TextField {...params} id="work-entry-payment-date" />;
                    }}
                />

                <DatePicker
                    label={"Rev. Rec. Date"}
                    value={rev_Rec_Date}
                    onChange={(e) => setRevRecDate(e)}
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                        params.fullWidth = true;
                        params.size = "small";
                        return <TextField {...params} id="work-entry-rev-rec-date" />;
                    }}
                />

                <TextField
                    id="work-entry-notes"
                    label="Notes"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />

                <CclDrawerActionBar>
                    <CclButton mode="primary" disabled={!canSave} onClick={handleSave}>
                        Save
                    </CclButton>
                    <CclButton mode="secondary" onClick={props.onClose}>
                        Cancel
                    </CclButton>
                </CclDrawerActionBar>
            </Stack>
        </CclDrawer>
         
    );
};

export default ModifyWorkEntryDrawer;
